import { create } from 'zustand'
import type { ThemeAccent, SidebarCollapsedState } from '@/types'

const THEME_STORAGE_KEY = 'crm_theme'
const SIDEBAR_STORAGE_KEY = 'crm-sidebar-collapsed'
const BG_STORAGE_KEY = 'artech_bg'

// Selectable app backgrounds. 'default' = the existing photo backdrop (solid
// cards); 'none' = plain cloud; the rest are colourful gradients that turn on
// the frosted-glass surfaces (`has-bg-image`). 'midnight' also sets `bg-is-dark`.
export const APP_BACKGROUNDS = [
  { key: 'default',  label: 'Default',  swatch: 'linear-gradient(135deg,#f4f8ff,#e8edf5)' },
  { key: 'none',     label: 'Plain',    swatch: '#f4f8ff' },
  { key: 'ocean',    label: 'Ocean',    swatch: 'linear-gradient(135deg,#0891b2,#1A6AB4)' },
  { key: 'forest',   label: 'Forest',   swatch: 'linear-gradient(135deg,#15803d,#2DB37A)' },
  { key: 'aurora',   label: 'Aurora',   swatch: 'linear-gradient(135deg,#3DC7B3,#7C3AED)' },
  { key: 'sunset',   label: 'Sunset',   swatch: 'linear-gradient(135deg,#d97706,#E11D48)' },
  { key: 'midnight', label: 'Midnight', swatch: 'linear-gradient(135deg,#1e1b4b,#4c1d95)' },
] as const

interface UIState {
  sidebarCollapsed: SidebarCollapsedState
  isSidebarMini: boolean
  isDarkMode: boolean
  accentColor: ThemeAccent
  appBackground: string
  isMobileSidebarOpen: boolean
  isMobileDrawerOpen: boolean

  // Actions
  toggleSection: (sectionKey: string) => void
  setSectionCollapsed: (sectionKey: string, collapsed: boolean) => void
  toggleSidebarMini: () => void
  setDarkMode: (isDark: boolean) => void
  toggleDarkMode: () => void
  setAccent: (color: ThemeAccent) => void
  setAppBackground: (bg: string) => void
  loadTheme: () => void
  saveSidebarState: () => void
  loadSidebarState: () => void
  setMobileSidebarOpen: (open: boolean) => void
  setMobileDrawerOpen: (open: boolean) => void
}

function applyDarkMode(isDark: boolean) {
  const html = document.documentElement
  if (isDark) {
    html.classList.add('dark')
  } else {
    html.classList.remove('dark')
  }
}

function applyAccent(color: ThemeAccent) {
  const html = document.documentElement
  if (color === 'blue') {
    html.removeAttribute('data-accent')
  } else {
    html.setAttribute('data-accent', color)
  }
}

function applyBackground(bg: string) {
  const html = document.documentElement
  if (!bg || bg === 'default') {
    html.removeAttribute('data-bg')
    html.classList.remove('has-bg-image', 'bg-is-dark')
  } else if (bg === 'none') {
    html.setAttribute('data-bg', 'none')
    html.classList.remove('has-bg-image', 'bg-is-dark')
  } else {
    html.setAttribute('data-bg', bg)
    html.classList.add('has-bg-image')
    html.classList.toggle('bg-is-dark', bg === 'midnight')
  }
}

function saveTheme(isDarkMode: boolean, accentColor: ThemeAccent) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, JSON.stringify({ isDarkMode, accentColor }))
  } catch {
    // Ignore storage errors
  }
}

export const useUIStore = create<UIState>((set, get) => ({
  sidebarCollapsed: {},
  isSidebarMini: false,
  isDarkMode: false,
  accentColor: 'blue',
  appBackground: 'default',
  isMobileSidebarOpen: false,
  isMobileDrawerOpen: false,

  toggleSection: (sectionKey: string) => {
    set((state) => {
      const newCollapsed = {
        ...state.sidebarCollapsed,
        [sectionKey]: !state.sidebarCollapsed[sectionKey],
      }
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(newCollapsed))
      } catch {
        // Ignore
      }
      return { sidebarCollapsed: newCollapsed }
    })
  },

  toggleSidebarMini: () => {
    const newMini = !get().isSidebarMini
    try { localStorage.setItem('crm-sidebar-mini', JSON.stringify(newMini)) } catch { }
    set({ isSidebarMini: newMini })
  },

  setSectionCollapsed: (sectionKey: string, collapsed: boolean) => {
    set((state) => {
      const newCollapsed = { ...state.sidebarCollapsed, [sectionKey]: collapsed }
      try {
        localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(newCollapsed))
      } catch {
        // Ignore
      }
      return { sidebarCollapsed: newCollapsed }
    })
  },

  setDarkMode: (isDark: boolean) => {
    applyDarkMode(isDark)
    set({ isDarkMode: isDark })
    saveTheme(isDark, get().accentColor)
  },

  toggleDarkMode: () => {
    const newIsDark = !get().isDarkMode
    applyDarkMode(newIsDark)
    set({ isDarkMode: newIsDark })
    saveTheme(newIsDark, get().accentColor)
  },

  setAccent: (color: ThemeAccent) => {
    applyAccent(color)
    set({ accentColor: color })
    saveTheme(get().isDarkMode, color)
  },

  setAppBackground: (bg: string) => {
    applyBackground(bg)
    set({ appBackground: bg })
    try { localStorage.setItem(BG_STORAGE_KEY, bg) } catch { /* ignore */ }
  },

  loadTheme: () => {
    try {
      const savedBg = localStorage.getItem(BG_STORAGE_KEY) || 'default'
      applyBackground(savedBg)
      set({ appBackground: savedBg })
    } catch { /* ignore */ }
    try {
      const stored = localStorage.getItem(THEME_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as { isDarkMode?: boolean; accentColor?: ThemeAccent }
        const isDark = parsed.isDarkMode ?? false
        const accent = parsed.accentColor ?? 'blue'
        applyDarkMode(isDark)
        applyAccent(accent)
        set({ isDarkMode: isDark, accentColor: accent })
      } else {
        // Check system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        applyDarkMode(prefersDark)
        set({ isDarkMode: prefersDark })
      }
    } catch {
      // Use defaults
    }
  },

  saveSidebarState: () => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, JSON.stringify(get().sidebarCollapsed))
    } catch {
      // Ignore
    }
  },

  loadSidebarState: () => {
    try {
      const stored = localStorage.getItem(SIDEBAR_STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as SidebarCollapsedState
        set({ sidebarCollapsed: parsed })
      }
      const miniStored = localStorage.getItem('crm-sidebar-mini')
      if (miniStored) set({ isSidebarMini: JSON.parse(miniStored) })
    } catch {
      // Use defaults
    }
  },

  setMobileSidebarOpen: (open: boolean) => {
    set({ isMobileSidebarOpen: open })
  },

  setMobileDrawerOpen: (open: boolean) => {
    set({ isMobileDrawerOpen: open })
  },
}))
